/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Extracts the signing time (`genTime`) from an RFC 3161 TimeStampToken — a CMS
 * SignedData (PKCS#7) whose eContent is a TSTInfo. We only read the time here;
 * TSA-signature trust is a step-7 concern.
 */

import { readTlv, readChildren, content, type Tlv } from './der'

const ID_SIGNED_DATA = '1.2.840.113549.1.7.2'
const ID_CT_TSTINFO = '1.2.840.113549.1.9.16.1.4'

function oidOf (u8: Uint8Array, t: Tlv): string {
    // local OID decode to avoid importing decodeOid for one call
    const b = content(u8, t)
    if (b.length === 0) return ''
    const parts = [Math.floor(b[0] / 40), b[0] % 40]
    let v = 0
    for (let i = 1; i < b.length; i++) {
        v = v * 128 + (b[i] & 0x7f)
        if (!(b[i] & 0x80)) { parts.push(v); v = 0 }
    }
    return parts.join('.')
}

/**
 * Robustly parses an ASN.1 GeneralizedTime into an ISO-8601 UTC string, or null
 * if it is not a well-formed UTC instant. RFC 3161 §2.4.2 mandates the full
 * `YYYYMMDDHHMMSS[.fff]Z` form (seconds present, `Z` suffix); fractional seconds
 * are dropped. A value lacking seconds, lacking `Z`, or naming an impossible
 * date (month 13, day 32, hour 24, rolled-over) is rejected so a malformed token
 * can be ignored rather than yielding a garbage reference time (§15.8.2).
 */
export function parseGeneralizedTime (s: string): string | null {
    const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:\.\d+)?Z$/.exec(s)
    if (!m) return null
    const [, y, mo, d, h, mi, se] = m
    const iso = `${y}-${mo}-${d}T${h}:${mi}:${se}+00:00`
    const ms = Date.parse(iso)
    if (Number.isNaN(ms)) return null
    // Reject values JS silently rolls over (e.g. month 13 → next January).
    const back = new Date(ms)
    if (back.getUTCFullYear() !== +y || back.getUTCMonth() + 1 !== +mo || back.getUTCDate() !== +d ||
        back.getUTCHours() !== +h || back.getUTCMinutes() !== +mi || back.getUTCSeconds() !== +se) return null
    return iso
}

/**
 * Locates the CMS SignedData ContentInfo within a token that may be either a bare
 * TimeStampToken (ContentInfo) or a full RFC 3161 TimeStampResp (status +
 * timeStampToken). Returns the ContentInfo SEQUENCE TLV.
 */
function findContentInfo (u8: Uint8Array, seq: Tlv): Tlv | null {
    const kids = readChildren(u8, seq.contentStart, seq.contentEnd)
    if (kids[0]?.tag === 0x06 && oidOf(u8, kids[0]) === ID_SIGNED_DATA) return seq
    // TimeStampResp ::= SEQUENCE { status PKIStatusInfo, timeStampToken ContentInfo OPTIONAL }
    for (const k of kids) {
        if (k.tag === 0x30) {
            const sub = readChildren(u8, k.contentStart, k.contentEnd)
            if (sub[0]?.tag === 0x06 && oidOf(u8, sub[0]) === ID_SIGNED_DATA) return k
        }
    }
    return null
}

/** Returns the TSTInfo `genTime` as ISO-8601, or null if it can't be located. */
export function extractTstGenTime (token: Uint8Array): string | null {
    try {
        const textDecoder = new TextDecoder()
        // ContentInfo ::= SEQUENCE { contentType OID, content [0] EXPLICIT }
        const ci = findContentInfo(token, readTlv(token, 0))
        if (!ci) return null
        const ciKids = readChildren(token, ci.contentStart, ci.contentEnd)
        if (oidOf(token, ciKids[0]) !== ID_SIGNED_DATA) return null
        const content0 = ciKids[1] // [0] EXPLICIT
        const signedData = readChildren(token, content0.contentStart, content0.contentEnd)[0]
        const sdKids = readChildren(token, signedData.contentStart, signedData.contentEnd)
        // SignedData: version, digestAlgorithms SET, encapContentInfo SEQ, …
        const encap = sdKids.find(k => (k.tag & 0x1f) === 0x10 && k.tag === 0x30) // first SEQUENCE after the SET
            ?? sdKids[2]
        const encapKids = readChildren(token, encap.contentStart, encap.contentEnd)
        if (oidOf(token, encapKids[0]) !== ID_CT_TSTINFO) return null
        // eContent [0] EXPLICIT OCTET STRING containing TSTInfo
        const eContent = encapKids[1]
        const octet = readChildren(token, eContent.contentStart, eContent.contentEnd)[0]
        const tstInfo = readTlv(token, octet.contentStart)
        // TSTInfo: version, policy, messageImprint, serialNumber, genTime GeneralizedTime(0x18)
        const genTime = readChildren(token, tstInfo.contentStart, tstInfo.contentEnd).find(k => k.tag === 0x18)
        if (!genTime) return null
        return parseGeneralizedTime(textDecoder.decode(content(token, genTime)))
    } catch {
        return null
    }
}
